import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./admin.css";
import "../plugins/custom_css/dashboard.css";
import { getAuthHeaders } from "../utils/authUtils";
import { generatePageNumbers } from "../utils/pagination";
import BlogsCRUD from "./AllCurds/BlogsCRUD";
import EventsCRUD from "./AllCurds/EventCRUD";
import NoticeCRUD from "./AllCurds/NoticeCRUD";
import FAQCRUD from "./AllCurds/FaqCRUD";
import { BannerCrud } from "./AllCurds/BannerCRUD";
import { TestimonialCrud } from "./AllCurds/TestimonialCRUD";
import ServicesCRUD from "./AllCurds/ServicesCRUD";
import QueriesCRUD from "./AllCurds/QueriesCRUD";
import CoursesCRUD from "./AllCurds/CoursesCRUD";
import CrmContainer from "./crm/CrmContainer";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "../components/ui/sidebar";
import { Toaster } from "../components/ui/sonner";
import AppSidebar from "../components/admin/AppSidebar";
import { TrendingUp, Clock, Users } from "lucide-react";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;

const TAB_LABELS = {
  dashboard: "Dashboard", service: "Services", blogs: "Blogs", courses: "Courses",
  notices: "Notices", events: "Events", queries: "Queries", faqs: "FAQ's",
  banners: "Banners", testimonials: "Testimonials", crm: "CRM",
};

export const AdminDashboard = ({ logout }) => {
  useEffect(() => { document.title = "Admin Dashboard - Smart Scholars 360"; }, []);

  const navigate = useNavigate();

  const handleAutoLogout = useCallback(() => { logout(); navigate("/"); }, [logout, navigate]);

  const checkTokenExpiry = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) { handleAutoLogout(); return false; }
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload.exp <= Math.floor(Date.now() / 1000) + 30) { handleAutoLogout(); return false; }
      return true;
    } catch { handleAutoLogout(); return false; }
  }, [handleAutoLogout]);

  useEffect(() => {
    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 60000);
    return () => clearInterval(interval);
  }, [checkTokenExpiry]);

  const handleLogout = () => { logout(); navigate("/"); };

  const [activeTab, setActiveTab] = useState("dashboard");
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({ total: 0, pending: 0, resolved: 0 });
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0, total_pages: 1 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (activeTab === "dashboard") { fetchQueries(); fetchStats(); } }, [activeTab]);

  const makeAuthenticatedRequest = async (fn) => {
    if (!checkTokenExpiry()) return;
    try { return await fn(); }
    catch (err) { if (err.response?.status === 401) { handleAutoLogout(); return; } throw err; }
  };

  const fetchQueries = async (page = 1) => {
    await makeAuthenticatedRequest(async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${BASE_URI}/queries?page=${page}&limit=${pagination.page_size}`, getAuthHeaders());
        if (res.data?.data) {
          setQueries(res.data.data.map((item) => ({
            id: item.id, name: item.data.name || "N/A", email: item.data.email || "N/A",
            status: item.data.status || "new", created_at: item.data.created_at || "Unknown",
          })));
          setPagination({ page: res.data.page, page_size: res.data.limit, total: res.data.total, total_pages: Math.ceil(res.data.total / res.data.limit) });
        }
        setError("");
      } catch { setError("Failed to fetch queries"); }
      finally { setLoading(false); }
    });
  };

  const fetchStats = async () => {
    await makeAuthenticatedRequest(async () => {
      try {
        const res = await axios.get(`${BASE_URI}/queries?limit=1000`, getAuthHeaders());
        if (res.data?.data) {
          const all = res.data.data.map((i) => ({ status: i.data.status || "new" }));
          setStats({
            total: all.length,
            pending: all.filter((q) => ["new", "pending"].includes(q.status.toLowerCase())).length,
            resolved: all.filter((q) => q.status.toLowerCase() === "resolved").length,
          });
        }
      } catch { /* silent */ }
    });
  };

  const handlePageChange = (p) => { if (p >= 1 && p <= pagination.total_pages) fetchQueries(p); };

  const statusVariant = (s) => ({ resolved: "resolved", pending: "pending", new: "new" }[s?.toLowerCase()] ?? "secondary");
  const formatDate = (d) => {
    if (!d || d === "Unknown") return "—";
    try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return d; }
  };

  const statCards = [
    { label: "Total Queries",    value: stats.total,    icon: Users,      color: "text-blue-600",   bg: "bg-blue-50",   bar: "bg-blue-500" },
    { label: "Pending Queries",  value: stats.pending,  icon: Clock,      color: "text-yellow-600", bg: "bg-yellow-50", bar: "bg-yellow-500" },
    { label: "Resolved Queries", value: stats.resolved, icon: TrendingUp, color: "text-green-600",  bg: "bg-green-50",  bar: "bg-green-500" },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard": return (
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <h1 className="text-xl font-bold text-gray-900 leading-tight">Dashboard</h1>
            <p className="mt-0.5 text-[13px] text-gray-500">Overview of queries and site activity</p>
          </div>
          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {statCards.map((s) => (
              <Card key={s.label} className="overflow-hidden">
                <div className={`h-1 w-full ${s.bar}`} />
                <CardContent className="p-5 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">{s.label}</p>
                    <p className="text-3xl font-extrabold text-gray-900">{s.value > 999 ? "999+" : s.value}</p>
                  </div>
                  <div className={`h-11 w-11 rounded-xl ${s.bg} flex items-center justify-center`}>
                    <s.icon className={`h-5 w-5 ${s.color}`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Recent Queries</h2>
                <p className="text-xs text-gray-500 mt-0.5">Latest contact submissions</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setActiveTab("queries")}>View all</Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead>
                  <TableHead>Date</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-gray-400">
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />Loading…
                    </div>
                  </TableCell></TableRow>
                ) : queries.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-gray-400">No queries found</TableCell></TableRow>
                ) : queries.map((q) => (
                  <TableRow key={q.id}>
                    <TableCell className="font-medium text-gray-900">{q.name}</TableCell>
                    <TableCell>{q.email}</TableCell>
                    <TableCell>{formatDate(q.created_at)}</TableCell>
                    <TableCell><Badge variant={statusVariant(q.status)}>{q.status === "resolved" ? "Resolved" : q.status === "pending" ? "Processing" : "New"}</Badge></TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => setActiveTab("queries")}>View →</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-center gap-1 p-4 border-t border-gray-100">
                <Button size="sm" variant="outline" onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1 || loading}>Previous</Button>
                {generatePageNumbers(pagination.page, pagination.total_pages).map((p, i) => (
                  p === "..." ? <span key={i} className="px-2 text-gray-400 text-sm">…</span> : (
                    <Button key={i} size="sm" variant={p === pagination.page ? "default" : "outline"} onClick={() => handlePageChange(p)} disabled={loading}>{p}</Button>
                  )
                ))}
                <Button size="sm" variant="outline" onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.total_pages || loading}>Next</Button>
              </div>
            )}
          </Card>
        </div>
      );
      case "queries":      return <QueriesCRUD />;
      case "service":      return <ServicesCRUD />;
      case "courses":      return <CoursesCRUD />;
      case "blogs":        return <BlogsCRUD />;
      case "notices":      return <NoticeCRUD />;
      case "events":       return <EventsCRUD />;
      case "faqs":         return <FAQCRUD />;
      case "banners":      return <BannerCrud />;
      case "testimonials": return <TestimonialCrud />;
      case "crm":          return <CrmContainer />;
      default: return null;
    }
  };

  return (
    <SidebarProvider className="admin-layout">
      <Toaster />
      <AppSidebar activeTab={activeTab} onNavigate={setActiveTab} onLogout={handleLogout} />
      <SidebarInset>
        {/* Top bar */}
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4">
          <SidebarTrigger />
          <div className="h-4 w-px bg-gray-200" />
          <span className="text-sm font-medium text-gray-700">{TAB_LABELS[activeTab] || "Admin"}</span>
        </header>
        {/* Page content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderContent()}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};
